/**
 * Action wrapper for creating a standalone consultation for a patient.
 * Shows a confirmation dialog with a clinic picker, or Remote, before creating.
 *
 * Exports both a controlled dialog (`CreateConsultationDialog`) for use inside
 * dropdown menus and a self-contained action (`CreateConsultationAction`) with
 * a render prop trigger.
 */
import { useId, useState } from 'react'
import { toast } from 'sonner'

import type { ClinicId } from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { useActiveConsultation } from '@/frontend/features/consultations/active-consultation-context'
import { useCreateConsultation } from '@/frontend/hooks/api/use-create-consultation'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'

interface CreateConsultationDialogProps {
  patientId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Confirmation dialog that creates a standalone consultation at a clinic or remotely. */
export function CreateConsultationDialog({
  patientId,
  open,
  onOpenChange,
}: CreateConsultationDialogProps) {
  const [clinicId, setClinicId] = useState<ClinicId | null>(null)
  const [remote, setRemote] = useState(false)
  const remoteId = useId()
  const { consultationId, open: openConsultation } = useActiveConsultation()
  const { mutateAsync: createConsultation, isPending } = useCreateConsultation()
  const { data: staff } = useGetAuthenticatedStaff()

  async function handleConfirm() {
    if (!staff) throw new Error('Authenticated staff not available')
    if (consultationId !== null) {
      toast.warning('Please close the current consultation first')
      return
    }
    try {
      const { consultation } = await createConsultation({
        patientId,
        staffId: staff.id,
        ...(!remote && clinicId ? { clinicId } : {}),
      })
      openConsultation(consultation.id)
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create consultation')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create consultation</DialogTitle>
          <DialogDescription>
            This will create a standalone consultation, not linked to any appointment. Are you sure?
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-x-4">
          <span className="font-medium">Clinic:</span>
          <ClinicSearchWithDefault
            className="flex-1"
            value={clinicId}
            onChange={setClinicId}
            placeholder="Select clinic..."
            disabled={remote}
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={remoteId}
            checked={remote}
            onCheckedChange={(checked) => setRemote(checked === true)}
          />
          <label htmlFor={remoteId} className="text-sm cursor-pointer select-none">
            Remote consultation
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending || !staff || (!remote && !clinicId)}>
            {isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface CreateConsultationActionProps {
  patientId: string
  render: (props: { onClick: () => void }) => React.ReactNode
}

/** Self-contained action with render prop trigger and built-in confirmation dialog. */
export function CreateConsultationAction({ patientId, render }: CreateConsultationActionProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      {render({ onClick: () => setOpen(true) })}
      <CreateConsultationDialog patientId={patientId} open={open} onOpenChange={setOpen} />
    </>
  )
}
